import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Button, Col, Row } from '@arkestro/arkestro-design-system';
import { Radio } from 'antd';
import { UserContext } from '@providers/UserProvider/UserProvider';
import Footer from 'components/on_board/Footer/Footer';
import { getAllVendorOrgsList } from 'api/VendorDBApi';
import { updateVendorProfile } from 'api/VendorProfileDbApi';
import { isFreeEmail as checkIfIsFreeEmail } from 'utils/email';
import { CompanyCard } from './CompanyCard/CompanyCard';
import EmailCheckDisplay from './EmailCheckDisplay';
import style from './AffiliationConfirmation.module.scss';

const AffiliationConfirmation = ({
  currentStepName,
  updateOnboardStep,
  setProperty,
  userParams,
  vendorOrg,
}) => {
  const { t } = useTranslation('common');
  const currentUser = useContext(UserContext);
  const existingVendorOrgId = currentUser.vendor_profile?.vendor_organization_id;
  const [vendorOrgList, setVendorOrgList] = useState([vendorOrg]); // TODO: Need to move this to a provider
  const [displayList, setDisplayList] = useState(vendorOrgList);
  const [selectedVendorOrg, setSelectedVendorOrg] = useState(vendorOrg);
  const didSelectOrg = vendorOrg !== undefined && vendorOrg.id; // Vendor either selected an org or created an org
  const [matchedOrg, setMatchedOrg] = useState(vendorOrg);
  const [loading, setLoading] = useState(true);
  const [radio, setRadio] = useState(0);

  const handleClick = (e, value) => {
    const index = value - 1;

    setRadio(value || 0);
    setSelectedVendorOrg(index === -1 ? vendorOrg : displayList[index]);
  };

  const goBack = () => {
    setProperty({ currentStepName: 'intro', vendorOrg: undefined });
    setLoading(true);
  };

  // Get list of vendor orgs to do an email match, or return the org the supplier already has
  useEffect(() => {
    setLoading(true);

    // Immediately check if the user email is free email so it's available when confirming organization
    setProperty({ userParams: { ...userParams, isFreeEmail: checkIfIsFreeEmail(currentUser.email) } });

    getAllVendorOrgsList()
      .then(response => {
        if (!response.success) return;

        const responseOrgList = response.company_vendor_orgs;

        setTimeout(() => {
          if (responseOrgList && responseOrgList.length === 0) {
            setProperty({ currentOrgStep: 'no_org_match' }); // No vendor orgs, so go ahead and advance to create an org
            return;
          }

          const foundMatchedOrg = responseOrgList.find(org => org.id === currentUser.vendor_profile?.vendor_organization_id);

          setVendorOrgList(responseOrgList);

          if (foundMatchedOrg && foundMatchedOrg.name !== currentUser.email) {
            setSelectedVendorOrg(foundMatchedOrg);
            setMatchedOrg(foundMatchedOrg);
          }

          const filteredResponseOrgList = responseOrgList.filter(org => org.id !== existingVendorOrgId);

          setDisplayList(filteredResponseOrgList);

          // Need to see why the vendorOrg NEEDS to be set for this to render
          if (!foundMatchedOrg && responseOrgList.length === 0) {
            setProperty({ currentOrgStep: 'no_org_match', updateOrgId: currentUser.vendor_profile?.vendor_organization_id });
          } else {
            setProperty({
              vendorOrg: {
                id: responseOrgList[0].id,
                name: responseOrgList[0].name,
              }
            }); // set the vendor org that matches to context
          }
        }, 3000);
      });

    setLoading(false);
  }, []);

  // Changing confirm to pull from radio selection to get the ID, not use the vendorOrg stuff that's currently here
  const confirmOrganization = () => {
    if (!selectedVendorOrg || !selectedVendorOrg.id) return;

    // Attach vendor org to current vendor user's profile
    updateVendorProfile({ email: currentUser.email, vendorOrgId: selectedVendorOrg.id }, userParams.isFreeEmail)
      .then(response => {
        if (!response.success) return;
        updateOnboardStep(currentStepName); // updates onboarding state, which advances to next step
        setProperty({ updateOrgId: selectedVendorOrg.id });
      });
  };

  if ((!didSelectOrg && !vendorOrgList.length === 0) || !vendorOrg) return <EmailCheckDisplay />; // Still fetching vendor org list

  return (
    <>
      <Row>
        <Col xs={24}>
          <div className={style.organizationHeader}>
            <span className={style.title}>
              {t('onboarding.supplier.affiliation.select_affiliation')}
            </span>
            <p className={style.email}>
              {currentUser.email}
            </p>
          </div>
        </Col>
      </Row>
      <Row className={style.orgsContainer}>
        <Col xs={24}>
          <Radio.Group
            className={style.orgRadioGroup}
            size="large"
            value={radio}
          >
            {matchedOrg && (
              <div className={style.defaultOrg}>
                <p className={style.defaultOrgText}>
                  {t('onboarding.supplier.affiliation.default_org_text')}
                </p>
                <CompanyCard company={matchedOrg} dataTestId='default-org-card' onClick={e => handleClick(e, 0)} selectedVendorOrg={0} />
              </div>
            )}
            {displayList.length > 0 && matchedOrg && !loading && (
              <Row>
                <Col xs={24}>
                  <span className={style.otherOrganizations}>
                    {t('onboarding.supplier.affiliation.other_organizations')}
                  </span>
                </Col>
              </Row>
            )}
            <div className={style.vendorOrgs}>
              {displayList.length > 0 && !loading
              && displayList.map((key, i) => {
                return (
                  <CompanyCard key={key} company={key} dataTestId='other-org-card' onClick={e => handleClick(e, i+1)} selectedVendorOrg={i + 1} />
                );
              })}
            </div>
          </Radio.Group>
        </Col>
      </Row>
      <Button
        block
        onClick={() => setProperty({ currentOrgStep: 'create_org', updateOrgId: null })}
      >
        {t('onboarding.supplier.affiliation.create_new_organization')}
      </Button>
      <div className={style.buttons}>
        <Button
          onClick={goBack}
        >
          {t('general.back')}
        </Button>
        <Button
          onClick={confirmOrganization}
          type="primary"
        >
          {t('general.continue')}
        </Button>
      </div>
      <Footer />
    </>
  );
};

AffiliationConfirmation.propTypes = {
  currentStepName: PropTypes.string.isRequired,
  setProperty: PropTypes.func.isRequired,
  updateOnboardStep: PropTypes.func.isRequired,
  userParams: PropTypes.shape({
    isFreeEmail: PropTypes.bool.isRequired,
  }),
  vendorOrg: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    name: PropTypes.string
  })
};

export default AffiliationConfirmation;
